import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, ReplaySubject, Subject } from 'rxjs';
import { tap, shareReplay, catchError, filter, take, map } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environments';

/** Índice estación → grupo (o subgrupo) y catálogo del filtro «Grupo». */
export interface GroupIndex {
  byStation: Map<string, string>;
  options: { value: string; label: string }[];
  /** Grupos encontrados (id canónico → etiqueta), aunque sea uno solo: lo que `options` omite cuando no hay de dónde elegir. */
  labels?: { value: string; label: string }[];
}

/**
 * Arma el índice del filtro «Grupo» a partir de filas que traen group/group_name
 * y, opcionalmente, el subgrupo (cluster): `clusters[0]` en el perfil, o
 * `cluster`/`cluster_name` en las filas de los endpoints. Top Gas y Garbri viven
 * DENTRO del grupo Mobico como clusters, y el filtro debe mostrarlos aparte.
 * La clave es el ID del cluster (o del grupo); el nombre es solo etiqueta. Dos
 * ids con el MISMO nombre (grupo Garbri y cluster Garbri) se funden en una sola
 * opción, con el primer id como canónico, para no repetir «Garbri» en la lista.
 */
export function buildGroupIndex(rows: any[], idOf: (row: any) => string): GroupIndex {
  const byStation = new Map<string, string>();
  const canonicalByLabel = new Map<string, string>();
  const labelByValue = new Map<string, string>();
  (rows || []).forEach((row: any) => {
    if (!row?.group) { return; }
    const cluster = row.clusters?.[0] ?? (row.cluster ? { _id: row.cluster, name: row.cluster_name } : null);
    const id = String(cluster?._id || row.group);
    const label = cluster?.name || row.group_name;
    if (!label) { return; }
    const value = canonicalByLabel.get(label) ?? id;
    canonicalByLabel.set(label, value);
    labelByValue.set(value, label);
    byStation.set(idOf(row), value);
  });
  const options = labelByValue.size > 1
    ? [
        { value: '', label: 'Todos los grupos' },
        ...[...labelByValue.entries()]
          .map(([value, label]) => ({ value, label }))
          .sort((a, b) => a.label.localeCompare(b.label, 'es'))
      ]
    : [];
  const labels = [...labelByValue.entries()].map(([value, label]) => ({ value, label }));
  return { byStation, options, labels };
}

/**
 * Une varios índices de grupo (perfil + filas de cada carga) en un solo catálogo. El ADMIN
 * pide con all_groups y el catálogo salía SOLO de las filas de la carga: con una semana en la
 * que todo lo cargado es de un grupo el filtro desaparecía (Edey 19-oct). Con la unión, el
 * catálogo conserva los grupos ya vistos (perfil y cargas anteriores) y el filtro se queda.
 * Mismo criterio que buildGroupIndex: dos ids con la misma etiqueta son una sola opción.
 */
export function mergeGroupIndexes(...parts: (GroupIndex | null | undefined)[]): GroupIndex {
  const canonicalByLabel = new Map<string, string>();
  const byStation = new Map<string, string>();
  parts.filter((p): p is GroupIndex => !!p).forEach((p) => {
    const labelOf = new Map((p.labels || p.options.filter((o) => o.value)).map((o) => [o.value, o.label]));
    labelOf.forEach((label, value) => { if (!canonicalByLabel.has(label)) { canonicalByLabel.set(label, value); } });
    p.byStation.forEach((value, station) => {
      const label = labelOf.get(value);
      byStation.set(station, (label && canonicalByLabel.get(label)) || value);
    });
  });
  const labels = [...canonicalByLabel.entries()].map(([label, value]) => ({ value, label }));
  const options = labels.length > 1
    ? [{ value: '', label: 'Todos los grupos' }, ...[...labels].sort((a, b) => a.label.localeCompare(b.label, 'es'))]
    : [];
  return { byStation, options, labels };
}

/**
 * Filtro «Grupo» COMPARTIDO entre las vistas del flujo de pipas (Análisis de Pedido,
 * Inventario Real y Teórico, Solicitud de pipas y el panel admin). Edey 16-oct: «si pongo
 * grupo Garbri en Análisis, que se vea en Solicitud de pipas y en Inventario Real y
 * Teórico». Se guarda la ETIQUETA, no el id: cada vista arma su catálogo por su lado
 * (perfil o filas) y el id canónico de «Garbri» puede cambiar entre ellas, la etiqueta no.
 * sessionStorage: vive mientras dure la pestaña (sobrevive a los reload de openPipesStation).
 */
const GROUP_FILTER_LABEL_KEY = 'pipesGroupFilterLabel';

/**
 * Aviso EN VIVO del cambio (etiqueta; '' = todos). sessionStorage solo se lee al cargar, y en la
 * Solicitud de pipas del admin conviven dos vistas a la vez (panel + Cronograma): sin esto la otra
 * no se movía hasta recargar (Edey 21-oct; en el solicitante es un solo componente).
 */
const groupFilterLabel$ = new Subject<string>();
export const groupFilterChanges$ = groupFilterLabel$.asObservable();

/** Guarda la opción elegida ('' limpia). Llamar desde el (ngModelChange) del filtro. */
export function rememberGroupFilter(options: { value: string; label: string }[], value: string): void {
  const label = value ? options.find((o) => o.value === value)?.label || '' : '';
  try {
    if (label) { sessionStorage.setItem(GROUP_FILTER_LABEL_KEY, label); }
    else { sessionStorage.removeItem(GROUP_FILTER_LABEL_KEY); }
  } catch { /* sin storage: el filtro solo vive en la vista */ }
  groupFilterLabel$.next(label);
}

/** Valor de `options` para la etiqueta avisada; si esta vista no tiene ese grupo, se queda con `current`. */
export function groupFilterFromLabel(options: { value: string; label: string }[], label: string, current: string): string {
  if (!label) { return ''; }
  return options.find((o) => o.label === label)?.value ?? current;
}

/**
 * Valor que debe tomar el filtro con el catálogo recién armado: el guardado si su etiqueta
 * existe en `options`; si no, el actual mientras siga existiendo; si no, '' (todos).
 */
export function recallGroupFilter(options: { value: string; label: string }[], current: string): string {
  let label = '';
  try { label = sessionStorage.getItem(GROUP_FILTER_LABEL_KEY) || ''; } catch { label = ''; }
  const saved = label ? options.find((o) => o.label === label)?.value : undefined;
  if (saved) { return saved; }
  return current && options.some((o) => o.value === current) ? current : '';
}

export interface ProfileData {
  _id: string;
  email: string;
  name: string;
  rol: string;
  stations: any[];
  idStation?: string;
  [key: string]: any;
}

@Injectable({
  providedIn: 'root'
})
export class ProfileCacheService {
  private endpoint1 = environment.apiUrl + '/v1';
  private profileCache$: Observable<ProfileData> | null = null;
  private profileSubject = new BehaviorSubject<ProfileData | null>(null);
  private profileReadySubject = new ReplaySubject<boolean>(1);

  /**
   * Observable que emite los datos del perfil cuando cambian.
   * Emite null cuando no hay perfil cargado.
   */
  public profile$ = this.profileSubject.asObservable();

  /**
   * Observable que emite true cuando el perfil está completamente cargado.
   * Útil para esperar a que el perfil esté listo antes de ejecutar otras acciones.
   */
  public profileReady$ = this.profileReadySubject.asObservable();

  constructor(private http: HttpClient) {
    // Marcar como no listo inicialmente
    this.profileReadySubject.next(false);
  }

  /**
   * Obtiene el perfil del usuario con caché automático.
   * Solo hace una llamada al servidor la primera vez, las llamadas subsiguientes
   * devuelven el valor cacheado.
   *
   * @param dios - Indica si el usuario tiene rol "Dios"
   * @param group - ID del grupo (opcional, requerido si dios es false)
   * @returns Observable con los datos del perfil
   */
  getProfile(dios: boolean, group?: string): Observable<ProfileData> {
    // Si ya existe un observable en cache, retornarlo
    if (this.profileCache$) {
      return this.profileCache$;
    }

    // Construir la URL según los parámetros
    const url = dios
      ? `${this.endpoint1}/crm/users/profile`
      : `${this.endpoint1}/crm/users/profile?group=${group}`;

    // Crear un nuevo observable con shareReplay para cachear el resultado
    this.profileCache$ = this.http.get<ProfileData>(url).pipe(
      tap(profile => {
        // Actualizar el BehaviorSubject con los datos del perfil
        this.profileSubject.next(profile);

        // Marcar el perfil como listo
        this.profileReadySubject.next(true);

        // Guardar datos importantes en localStorage para persistencia
        if (profile._id) {
          localStorage.setItem('id', profile._id);
        }
        if (profile.rol) {
          localStorage.setItem('rol', profile.rol);
        }
        if (profile.stations && profile.stations.length > 0) {
          localStorage.setItem('stations', JSON.stringify(profile.stations));
        }
      }),
      // shareReplay(1) mantiene el último valor emitido en cache
      // y lo comparte con todos los suscriptores
      shareReplay({ bufferSize: 1, refCount: false }),
      catchError(error => {
        // En caso de error, limpiar el cache para permitir reintentos
        this.clearCache();
        throw error;
      })
    );

    return this.profileCache$;
  }

  /**
   * Índice estación → grupo del usuario, derivado del perfil.
   *
   * La cuenta de un solicitante del flujo de pipas puede abarcar VARIOS grupos
   * (la piloto de MOBICO mezcla 22 estaciones de MOBICO con 3 de Garbri) y las
   * vistas que scopean con all_stations las listan todas juntas. Este índice es
   * lo que necesitan para ofrecer un filtro por grupo: el mapa para clasificar
   * cada fila y las opciones ya listas para el select.
   *
   * `options` viene VACÍO cuando el usuario abarca un solo grupo (o cuando el
   * back todavía no manda `group` por estación): en ese caso el filtro no
   * aporta nada y las vistas no lo pintan.
   *
   * Se apoya en el mismo getProfile cacheado, así que llamarlo desde varias
   * vistas no agrega peticiones.
   */
  getStationGroupIndex(
    dios: boolean,
    group?: string
  ): Observable<GroupIndex> {
    return this.getProfile(dios, group).pipe(
      map((profile: ProfileData) => buildGroupIndex(profile?.stations || [], (st) => String(st._id)))
    );
  }

  /**
   * Limpia el cache del perfil.
   * Útil cuando el usuario cierra sesión o cuando se necesita forzar
   * una nueva carga de datos del servidor.
   */
  clearCache(): void {
    this.profileCache$ = null;
    this.profileSubject.next(null);
    this.profileReadySubject.next(false);
  }

  /**
   * Obtiene el valor actual del perfil sin hacer petición al servidor.
   * Útil para acceso síncrono a datos ya cargados.
   *
   * @returns Los datos del perfil actual o null si no están cargados
   */
  getCurrentProfile(): ProfileData | null {
    return this.profileSubject.value;
  }

  /**
   * Actualiza parcialmente el perfil en cache sin hacer petición al servidor.
   * Útil para actualizaciones locales que no requieren sincronización inmediata.
   *
   * @param partialProfile - Objeto con las propiedades a actualizar
   */
  updateProfileLocally(partialProfile: Partial<ProfileData>): void {
    const current = this.profileSubject.value;
    if (current) {
      this.profileSubject.next({ ...current, ...partialProfile });
    }
  }

  /**
   * Verifica si el perfil está cargado en memoria
   *
   * @returns true si el perfil está cargado, false en caso contrario
   */
  isProfileLoaded(): boolean {
    return this.profileSubject.value !== null;
  }

  /**
   * Retorna un Observable que emite solo cuando el perfil está completamente cargado.
   * Útil para esperar a que el perfil esté listo antes de ejecutar otras operaciones.
   *
   * @returns Observable<ProfileData> que emite el perfil cuando está listo
   *
   * @example
   * // Esperar a que el perfil esté listo antes de cargar datos
   * this.profileCache.waitForProfile().subscribe(profile => {
   *   this.loadUserData(profile._id);
   * });
   */
  waitForProfile(): Observable<ProfileData> {
    return this.profile$.pipe(
      filter((profile): profile is ProfileData => profile !== null),
      take(1)
    );
  }

  /**
   * Ejecuta una función callback solo cuando el perfil está completamente cargado.
   * Si el perfil ya está cargado, ejecuta el callback inmediatamente.
   *
   * @param callback - Función a ejecutar con los datos del perfil
   *
   * @example
   * this.profileCache.whenReady(profile => {
   *   console.log('Perfil listo:', profile.name);
   *   this.loadDashboard(profile._id);
   * });
   */
  whenReady(callback: (profile: ProfileData) => void): void {
    const currentProfile = this.getCurrentProfile();
    if (currentProfile) {
      // Si ya está cargado, ejecutar inmediatamente
      callback(currentProfile);
    } else {
      // Si no, esperar a que se cargue
      this.waitForProfile().subscribe(callback);
    }
  }
}
